// ─── Scan data normalizer (single-machine and multi-machine) ────────────────────────
//
// PORT of `Report Generation Service/utils/scanDataNormalizer.js` — keep the two in
// sync. The AI report builder normalizes the scan payload server-side before it writes
// data paths ({{ scan:localhost.CVE_report... }}, table sourcePaths, network_devices),
// so the report builder MUST resolve those same paths against the normalized shape.
// Rendering against the raw multi-device envelope made every live path miss, which
// showed up as "N/A" tokens, empty tables, and empty charts in generated reports.
//
// The scanner's upload endpoint writes a multi-device envelope:
//   { localhosts: [ { device_name, hostname, scan_results: { localhost, network_devices } } ],
//     _metadata: { tenant_id, organization_name, total_devices, ... } }
//
// Everything downstream of this file (buildScanInsights, the widget extractors, the
// renderers, the legacy PDF template) was written against the older flat schema, where
// `localhost` is one machine object at the top level alongside `network_devices`.
//
// normalizeScanData() accepts either and always emits a superset shape:
//   • `localhost`       — all machines merged, so existing consumers keep working
//   • `network_devices` — all machines' devices concatenated + deduped
//   • `localhosts`      — normalized per-machine array for multi-machine-aware code
//   • `_fleet`          — compact per-machine summary (safe to put in an AI prompt)
//
// Findings inside the merged `localhost` carry a `device_name` so a merged report can
// still attribute each one to the machine it came from.

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

// Sections that describe the machine itself rather than findings — merging them would be
// meaningless, so keep the first machine's value and expose the rest under `<key>_by_device`.
const PER_DEVICE_SECTIONS = new Set([
    'Hostname',
    'IP',
    'Hardware Informations',
    'Windows_security',
    'IsDomainController',
]);

const SEVERITY_KEYS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

function severityRank(summaryBySeverity = {}) {
    if (summaryBySeverity.CRITICAL > 0) return 'CRITICAL';
    if (summaryBySeverity.HIGH > 0) return 'HIGH';
    if (summaryBySeverity.MEDIUM > 0) return 'MEDIUM';
    if (summaryBySeverity.LOW > 0) return 'LOW';
    return 'NONE';
}

function stamp(item, deviceName) {
    if (!isPlainObject(item)) return { value: item, device_name: deviceName };
    return { ...item, device_name: item.device_name || deviceName };
}

// ─── Per-section merge strategies ───────────────────────────────────────────────────

// The scanner writes the CVE payload under two different keys depending on which branch
// ran: `CVE_report` on workstations, `CVE Report` (with a space) on servers. Reading only
// one silently drops every vulnerability from the other kind of scan, with no error — the
// report just shows nothing. Accept both wherever CVE data is read.
function cveReportOf(localhost) {
    if (!isPlainObject(localhost)) return null;
    const cve = localhost.CVE_report ?? localhost['CVE Report'];
    return isPlainObject(cve) ? cve : null;
}

function mergeCveReport(machines) {
    const vulnerabilities = [];
    const bySeverity = Object.fromEntries(SEVERITY_KEYS.map((k) => [k, 0]));
    const bySource = {};
    let exploitable = 0;
    let patchable = 0;

    machines.forEach(({ device_name, localhost }) => {
        const cve = cveReportOf(localhost);
        if (!cve) return;

        const vulns = Array.isArray(cve.vulnerabilities) ? cve.vulnerabilities : [];
        vulns.forEach((v) => vulnerabilities.push(stamp(v, device_name)));

        const summary = isPlainObject(cve.summary) ? cve.summary : {};
        SEVERITY_KEYS.forEach((k) => {
            bySeverity[k] += Number(summary.by_severity?.[k]) || 0;
        });
        Object.entries(summary.by_source || {}).forEach(([k, n]) => {
            bySource[k] = (bySource[k] || 0) + (Number(n) || 0);
        });
        exploitable += Number(summary.exploitable) || 0;
        patchable += Number(summary.patchable) || 0;
    });

    // Prefer the machines' own reported totals; fall back to the concatenated length when a
    // machine shipped vulnerabilities without a summary block.
    const reportedTotal = machines.reduce(
        (sum, { localhost }) => sum + (Number(cveReportOf(localhost)?.summary?.total) || 0),
        0
    );

    return {
        vulnerabilities,
        summary: {
            total: reportedTotal || vulnerabilities.length,
            by_severity: bySeverity,
            by_source: bySource,
            exploitable,
            patchable,
            risk_level: severityRank(bySeverity),
        },
        by_device: machines
            .filter(({ localhost }) => cveReportOf(localhost))
            .map(({ device_name, localhost }) => {
                const cve = cveReportOf(localhost);
                return {
                    device_name,
                    total: Number(cve.summary?.total) || 0,
                    by_severity: cve.summary?.by_severity || {},
                    risk_level: cve.summary?.risk_level || null,
                };
            }),
    };
}

// HIBP_Scan is an object keyed by email address. The same address can appear on several
// machines, so union the keys and record which devices saw each one.
function mergeHibp(machines) {
    const merged = {};
    machines.forEach(({ device_name, localhost }) => {
        const hibp = localhost.HIBP_Scan;
        if (!isPlainObject(hibp)) return;
        Object.entries(hibp).forEach(([email, value]) => {
            if (!merged[email]) {
                merged[email] = isPlainObject(value) ? { ...value, devices: [] } : { value, devices: [] };
            }
            if (!merged[email].devices.includes(device_name)) {
                merged[email].devices.push(device_name);
            }
        });
    });
    return merged;
}

function mergeBrowserHistory(machines) {
    const merged = {};
    machines.forEach(({ device_name, localhost }) => {
        const history = localhost.Browser_History;
        if (!isPlainObject(history)) return;
        Object.entries(history).forEach(([bucket, entries]) => {
            if (!Array.isArray(merged[bucket])) merged[bucket] = [];
            if (Array.isArray(entries)) {
                entries.forEach((e) => merged[bucket].push(stamp(e, device_name)));
            }
        });
    });
    return merged;
}

// ─── Machine extraction ─────────────────────────────────────────────────────────────

function machineFromEnvelopeEntry(entry, index) {
    // Accept both the raw envelope entry ({ device_name, scan_results: {...} }) and an
    // entry this function already produced ({ device_name, localhost, network_devices }).
    // normalizeScanData must be idempotent: the payload now travels between the report
    // builder and the AI service already normalized, and re-normalizing an entry we
    // failed to recognise would silently empty `localhost` and `network_devices`.
    const scanResults = isPlainObject(entry?.scan_results)
        ? entry.scan_results
        : (isPlainObject(entry?.localhost) ? entry : {});
    const localhost = isPlainObject(scanResults.localhost) ? scanResults.localhost : {};
    return {
        device_name:
            entry?.device_name ||
            entry?.hostname ||
            localhost.Hostname ||
            localhost['Hardware Informations']?.hostname ||
            `device-${index + 1}`,
        hostname: entry?.hostname || localhost.Hostname || null,
        ip: localhost.IP || null,
        scan_timestamp: entry?.scan_timestamp || entry?.updated_at || entry?.first_seen || null,
        scanner_version: entry?.scanner_version || null,
        localhost,
        network_devices: Array.isArray(scanResults.network_devices) ? scanResults.network_devices : [],
    };
}

function machineFromFlatScan(raw) {
    const localhost = isPlainObject(raw.localhost) ? raw.localhost : {};
    const hw = localhost['Hardware Informations'] || {};
    return {
        device_name: localhost.Hostname || hw.hostname || hw.Hostname || 'localhost',
        hostname: localhost.Hostname || hw.hostname || hw.Hostname || null,
        ip: localhost.IP || null,
        scan_timestamp: raw.scan_timestamp || null,
        scanner_version: raw.scanner_version || null,
        localhost,
        network_devices: Array.isArray(raw.network_devices) ? raw.network_devices : [],
    };
}

// ─── Merging ────────────────────────────────────────────────────────────────────────

function mergeLocalhosts(machines) {
    if (machines.length === 0) return {};
    if (machines.length === 1) {
        // Nothing to merge, but still canonicalise the server-branch CVE key so a
        // single-machine server scan isn't left with data nothing downstream reads.
        const only = machines[0].localhost || {};
        if (only['CVE Report'] && !only.CVE_report) {
            const { 'CVE Report': serverCve, ...rest } = only;
            return { ...rest, CVE_report: serverCve };
        }
        return only;
    }

    const merged = {};
    const allKeys = new Set();
    machines.forEach(({ localhost }) => Object.keys(localhost || {}).forEach((k) => allKeys.add(k)));

    allKeys.forEach((key) => {
        // Sections with bespoke strategies.
        // Both scanner spellings collapse to the canonical `CVE_report` so downstream
        // consumers only ever have to know one key.
        if (key === 'CVE_report' || key === 'CVE Report') {
            merged.CVE_report = mergeCveReport(machines);
            return;
        }
        if (key === 'HIBP_Scan') {
            merged.HIBP_Scan = mergeHibp(machines);
            return;
        }
        if (key === 'Browser_History') {
            merged.Browser_History = mergeBrowserHistory(machines);
            return;
        }

        const present = machines.filter(({ localhost }) => localhost && localhost[key] !== undefined);
        if (present.length === 0) return;

        // Machine-identity sections: representative + per-device breakdown.
        if (PER_DEVICE_SECTIONS.has(key)) {
            merged[key] = present[0].localhost[key];
            merged[`${key}_by_device`] = present.map(({ device_name, localhost }) => ({
                device_name,
                value: localhost[key],
            }));
            return;
        }

        // Generic fallbacks so sections we haven't seen yet still survive the merge.
        const sample = present[0].localhost[key];

        if (Array.isArray(sample)) {
            const out = [];
            present.forEach(({ device_name, localhost }) => {
                const arr = localhost[key];
                if (Array.isArray(arr)) arr.forEach((item) => out.push(stamp(item, device_name)));
            });
            merged[key] = out;
            return;
        }

        if (isPlainObject(sample)) {
            const out = {};
            present.forEach(({ device_name, localhost }) => {
                const obj = localhost[key];
                if (!isPlainObject(obj)) return;
                Object.entries(obj).forEach(([k, v]) => {
                    if (out[k] === undefined) out[k] = v;
                });
            });
            merged[key] = out;
            merged[`${key}_by_device`] = present.map(({ device_name, localhost }) => ({
                device_name,
                value: localhost[key],
            }));
            return;
        }

        // Scalars.
        merged[key] = sample;
        merged[`${key}_by_device`] = present.map(({ device_name, localhost }) => ({
            device_name,
            value: localhost[key],
        }));
    });

    return merged;
}

function mergeNetworkDevices(machines) {
    const seen = new Map();
    machines.forEach(({ device_name, network_devices }) => {
        (network_devices || []).forEach((device) => {
            const key = device?.ip || device?.hostname || JSON.stringify(device);
            if (seen.has(key)) {
                // Same host reachable from several scanners — record the extra source.
                const existing = seen.get(key);
                if (!existing.source_devices.includes(device_name)) {
                    existing.source_devices.push(device_name);
                }
                return;
            }
            seen.set(key, {
                ...device,
                source_device: device_name,
                source_devices: [device_name],
            });
        });
    });
    return Array.from(seen.values());
}

// Compact, fixed-size summary per machine — this is what goes into the AI prompt instead
// of raw findings (the raw payload is multi-MB and would blow the context window).
function summarizeMachine(machine) {
    const lh = machine.localhost || {};
    const hw = lh['Hardware Informations'] || {};
    const cveSummary = cveReportOf(lh)?.summary || {};
    const openPorts = (machine.network_devices || []).reduce(
        (sum, d) => sum + Object.keys(d?.open_ports || {}).length,
        0
    );

    return {
        device_name: machine.device_name,
        hostname: machine.hostname,
        ip: machine.ip,
        scan_timestamp: machine.scan_timestamp,
        os: hw.OS || hw.os || hw['OS Name'] || null,
        os_version: hw['OS Version'] || null,
        manufacturer: hw.Manufacturer || null,
        model: hw.Model || null,
        domain: hw.Domain || null,
        ram: hw.RAM || null,
        cpu: hw.CPU || hw.Processor || null,
        cve_total: Number(cveSummary.total) || 0,
        cve_critical: Number(cveSummary.by_severity?.CRITICAL) || 0,
        cve_high: Number(cveSummary.by_severity?.HIGH) || 0,
        cve_medium: Number(cveSummary.by_severity?.MEDIUM) || 0,
        cve_risk_level: cveSummary.risk_level || null,
        breached_emails: isPlainObject(lh.HIBP_Scan) ? Object.keys(lh.HIBP_Scan).length : 0,
        saved_passwords: Array.isArray(lh.Browser_Password_analysis) ? lh.Browser_Password_analysis.length : 0,
        browser_extensions: Array.isArray(lh.Extensions) ? lh.Extensions.length : 0,
        network_devices_found: (machine.network_devices || []).length,
        open_ports_found: openPorts,
        defender_enabled: lh.Windows_security?.defender?.status?.AntivirusEnabled ?? null,
        realtime_protection: lh.Windows_security?.defender?.status?.RealTimeProtectionEnabled ?? null,
        tamper_protected: lh.Windows_security?.defender?.status?.IsTamperProtected ?? null,
        firewall: lh.Windows_security?.firewall ?? lh['Hardware Informations']?.Firewall ?? null,
    };
}

function buildFleetSummary(machines, metadata) {
    const perDevice = machines.map(summarizeMachine);
    const totals = perDevice.reduce(
        (acc, d) => ({
            cve_total: acc.cve_total + d.cve_total,
            cve_critical: acc.cve_critical + d.cve_critical,
            cve_high: acc.cve_high + d.cve_high,
            cve_medium: acc.cve_medium + d.cve_medium,
            breached_emails: acc.breached_emails + d.breached_emails,
            saved_passwords: acc.saved_passwords + d.saved_passwords,
            browser_extensions: acc.browser_extensions + d.browser_extensions,
            network_devices_found: acc.network_devices_found + d.network_devices_found,
            open_ports_found: acc.open_ports_found + d.open_ports_found,
        }),
        {
            cve_total: 0, cve_critical: 0, cve_high: 0, cve_medium: 0, breached_emails: 0,
            saved_passwords: 0, browser_extensions: 0, network_devices_found: 0, open_ports_found: 0,
        }
    );

    return {
        total_devices: machines.length,
        device_names: machines.map((m) => m.device_name),
        organization_name: metadata?.organization_name || null,
        tenant_name: metadata?.tenant_name || null,
        last_scan: metadata?.last_scan || null,
        totals,
        per_device: perDevice,
    };
}

// ─── Entry point ────────────────────────────────────────────────────────────────────

function normalizeScanData(raw) {
    if (!isPlainObject(raw)) return raw;

    let machines;
    if (Array.isArray(raw.localhosts)) {
        machines = raw.localhosts.map(machineFromEnvelopeEntry);
    } else if (isPlainObject(raw.localhost)) {
        machines = [machineFromFlatScan(raw)];
    } else {
        // Neither schema — nothing to normalize, pass through untouched.
        return raw;
    }

    if (machines.length === 0) return raw;

    const mergedLocalhost = mergeLocalhosts(machines);
    const mergedNetworkDevices = machines.length === 1
        ? machines[0].network_devices
        : mergeNetworkDevices(machines);

    return {
        ...raw,
        localhost: mergedLocalhost,
        network_devices: mergedNetworkDevices,
        localhosts: machines,
        _fleet: buildFleetSummary(machines, raw._metadata),
    };
}

export { normalizeScanData, summarizeMachine };
